import z from 'zod';
import { createModel, partySchema, QueryMany, QueryOne } from '../../common';
import { dateSchema, nameSchema, priceSchema } from '../../validation';
import { UTagSharedSchema } from '../utag';
import { languageSchema } from '../../common/language.schema';
import { leadStatusSchema } from './lead.status.model';
import { leadFunnelSchema } from './lead.funnel.model';
import { leadTimelineSchema } from './lead.timeline.model';
import { leadActionSchema } from './lead.actions';
import { leadMetaSchema } from './lead.meta';
import { leadPersonSchema } from './lead.person.model';
import { leadPrioritySchema } from './lead.priority.model';
import { leadInterestSchema } from './lead.interest.model';
import { leadLostReasonSchema } from './lead.lost-reason.model';
import { leadObjectionSchema } from './lead.objection.model';

/**
 * `Lead` collection — admin-only mini-CRM.
 *
 * Multi-purpose: used for landing-page demo bookings, on-ground sales
 * hunter submissions, inbound calls, referrals, and bulk imports. The
 * exact origin lives on `meta.source`.
 *
 * Hero is a network — every party can be in any role. A Lead can
 * therefore convert into an account of any shape: an Org (business
 * customer, carrier company, broker), a User (individual customer), or
 * a Driver (freelance owner-operator). See {@link converted}.
 */
export const LeadSchema = createModel({
  /**
   * Display title. Optional — the backend auto-derives a readable
   * value (`meta.companyName ?? people[0].name ?? people[0].phone ??
   * 'New lead'`) so submissions with only a phone still render.
   *
   * Re-derived once, at signup, when the derived value was the phone
   * number and the account brought a real name with it. A first WhatsApp
   * message carries only digits, so the row read `+9665…` for its whole
   * life even after the person told us who they were; that is the one
   * case where overwriting is strictly better than the alternative. An
   * admin-typed title is never touched.
   */
  title: nameSchema.optional(),
  /** Company-level metadata + lead origin (`meta.source`). Required. */
  meta: leadMetaSchema,
  /**
   * People attached to this lead. Each entry is `{ name?, phone?,
   * email?, position?, isPrimary?, notes? }`, all optional. Even a
   * single submission lives as a 1-item array.
   *
   * Convention: `people[0]` is the primary follow-up contact when no
   * entry is flagged `isPrimary`; the backend enforces "at most one
   * `isPrimary`" at write time.
   */
  people: z.array(leadPersonSchema).default([]),
  /**
   * Every platform identity this lead is known by — the Org their
   * signup created, the User behind the thread, the Driver row an
   * owner-operator bills as, an existing Org an admin linked for an
   * upsell. `Party.target` says which is which.
   *
   * One array instead of the four places identity used to hide:
   * `Lead.org`, `meta.sourceMeta.orgId`, `meta.sourceMeta.userId`, and
   * the `orgId` / `userId` / `driverId` trio inside the
   * `account_created` funnel step's meta. Each was written by exactly
   * one path, so the answer to "which account is this" depended on how
   * the lead had arrived, and a freelancer — a User and a Driver and no
   * Org — matched none of the three.
   *
   * Append-only through `upsertLeadParty` (`@hero/core`): identities
   * accumulate over a lead's life and every writer runs from a
   * different handler, so each has to be safe to run twice. This is
   * also the id list a lead's `Billing` rows are resolved through —
   * see `Billing.payer`.
   */
  parties: z.array(partySchema).default([]),
  /**
   * Current pipeline status.
   *
   * Mostly derived: `FunnelService` re-runs `deriveLeadStatus` against
   * the completed steps of every entry in {@link funnels} on each step
   * completion. Admins can still set it directly, and a terminal
   * admin-set status wins over derivation — see `deriveLeadStatus`.
   */
  status: leadStatusSchema,
  /**
   * Funnel progress, one entry per product.
   *
   * This is where "which product is this lead for" lives — there was no
   * such field before, and inferring it meant joining the lead's thread
   * to `Conversation.personaId`, a link only the agent and signup paths
   * ever stamp. An array rather than a
   * single funnel because a lead can meet us twice: a Wathiqa Plus
   * thread that later wants Hero is one person with two funnels, not two
   * leads.
   *
   * `status` derives from the BEST progress across all entries — someone
   * who paid on one product is `won` even if their other funnel sits at
   * `account_created`.
   */
  funnels: z.array(leadFunnelSchema).default([]),
  /**
   * The account this lead became — one of {@link parties}, promoted.
   *
   * Role-neutral, and that is the fix: it was `convertedOrg` and only an
   * Org could fill it, so the entire freelancer branch — the one the
   * Wathiqa Plus line sells hardest — converted into nothing. An
   * owner-operator's account is a `Driver`, an individual customer's is a
   * `User`, and both are conversions by every definition that matters
   * (they have an account, they can be charged, they have paid).
   *
   * Written by the chat-signup path and by the admin manual link.
   * {@link wonDate} is the timestamp — deliberately not duplicated here.
   */
  converted: partySchema.optional(),
  /**
   * AdminUser snapshot of the sales rep that owns this lead. Drives
   * "my leads" filtering and per-rep analytics.
   */
  assignedTo: partySchema.optional(),
  /** How urgently the lead should be chased. */
  priority: leadPrioritySchema.optional(),
  /**
   * What the lead wants — multiple entries allowed since a single
   * inbound often mixes intents (e.g. "book demo" AND
   * "fleet_management"). An empty / omitted array means no specific
   * intent was captured. Historic single-value `interest` docs are
   * migrated by wrapping the value into a 1-item array.
   */
  interests: z.array(leadInterestSchema).default([]),
  /**
   * Estimated commercial value of the lead, used for pipeline-value
   * analytics. Free-form number; no currency tracked here because the
   * platform is multi-currency at the contract layer and pipeline
   * estimates rarely justify the bookkeeping.
   */
  estimatedValue: priceSchema.optional(),
  /** Why the lead was lost — set together with terminal statuses. */
  lostReason: leadLostReasonSchema.optional(),
  /**
   * Offers this lead turned down, with the reason they gave.
   *
   * Append-only and non-terminal — see {@link leadObjectionSchema} for
   * why a decline is not a `lostReason`. Written by the agent when it
   * asks "why?" after a no; the array so a prospect who declines twice
   * leaves two data points rather than overwriting the first.
   */
  objections: z.array(leadObjectionSchema).default([]),
  /** Preferred follow-up language. */
  language: languageSchema.optional(),
  /** Next scheduled follow-up. */
  nextFollowUpDate: dateSchema.optional(),
  /**
   * When the lead converted (`status` flipped to `won`) — the single
   * timestamp for it, which is why {@link converted} carries no `at` of
   * its own. The backend
   * auto-stamps this to "now" on the transition from any non-`won`
   * status into `won` — both via {@link convertLink} and a direct
   * status patch. Admins may override / backdate via the update API
   * (e.g. recording a deal that closed off-platform last week).
   *
   * Read by analytics for time-to-win + monthly-win cohorts. Set once
   * on first conversion; later status changes do not clear it, so it
   * survives as a historical timestamp even if the lead is later
   * re-opened.
   */
  wonDate: dateSchema.optional(),
  /**
   * When the lead became terminal-lost (`status` flipped to `lost`,
   * `unqualified`, or `dead`). Auto-stamped on the first transition
   * into the terminal-lost bucket; transitions WITHIN the bucket
   * (e.g. `lost` → `unqualified`) preserve the original date so the
   * "when did we drop this lead" timestamp stays stable. Admins may
   * override / backdate via the update API.
   */
  lostDate: dateSchema.optional(),
  /** Logged actions on this lead (updates, calls, meetings, emails). */
  actions: z.array(leadActionSchema).default([]),
  /** Append-only audit log of every mutation. */
  timeline: z.array(leadTimelineSchema).default([]),
  /** Universal tags (free-form `namespace:value` strings). */
  UTags: z.array(UTagSharedSchema).default([]),
});

export type Lead = z.infer<typeof LeadSchema.model>;
export type LeadData = z.infer<typeof LeadSchema.data>;
export type LeadPartial = z.infer<typeof LeadSchema.partial>;
export type LeadQueryMany = QueryMany<Lead>;
export type LeadQueryOne = QueryOne<Lead>;
